import React, {useCallback, useEffect, useState} from 'react'
import {useLocation} from 'react-router-dom'
import {Box, Tab, Tabs} from '@mui/material'
import EmptyState from 'src/components-new/layout/EmptyState'
import {useAppSelector} from 'src/hooks'
import apiClient from 'src/services/apiClient'
import PrimaryAssumptionsPanel from './PrimaryAssumptionsPanel'
import SecondaryAssumptionsPanel from './SecondaryAssumptionsPanel'

type DiarySubTab = 'primary' | 'secondary'

/**
 * New-style equivalent of src/pages/therapyDiary/TherapyDiary.tsx — the "Diario paziente" page a
 * PATIENT-role user sees for their own diary (as opposed to DiarioTab.tsx, the operator-facing
 * per-patient tab inside PatientDetailNew).
 *
 * Split into two tabs, same Tabs/Tab convention as TherapiesNew.tsx's "Terapie"/"Terapie
 * Accessorie": "Terapie" (PrimaryAssumptionsPanel) keeps today's diary behavior scoped to primary
 * therapies only, and "Terapie Accessorie" (SecondaryAssumptionsPanel) is new — a per-therapy
 * reminders history, moved here from the "Assunzioni" sub-tab that used to live inside
 * SecondaryTherapyDetail on the /therapies page.
 */
export default function DiaryNew(): JSX.Element {
  // SecondaryAssumptionResponseNew's "Prossima assunzione" link lands here wanting the
  // "Terapie Accessorie" tab open directly, and already scoped to that same reminder's therapy,
  // rather than making the patient re-select both. No pre-set "Stato" filter on top of that: the
  // patient should see this therapy's assumptions unfiltered, same as picking the tab manually.
  const location = useLocation<{tab?: DiarySubTab; secondaryTherapyId?: string} | undefined>()
  // Sub-items of "Diario paziente" in the PSP configurator (sidebarItems.therapyDiaryTherapies /
  // therapyDiarySecondaryTherapies). Only an explicit `show: false` hides a tab: PSPs configured
  // before these sub-items existed don't have the keys at all and must keep seeing both tabs.
  const sidebarItems = useAppSelector((state) => state.pspConfigReducer.data.sidebarItems)
  const showPrimaryTab = sidebarItems?.therapyDiaryTherapies?.show !== false
  const showSecondaryTab = sidebarItems?.therapyDiarySecondaryTherapies?.show !== false
  const [secondaryTherapies, setSecondaryTherapies] = useState<Record<string, any>[]>([])
  // Distinct from `secondaryTherapies.length === 0`: that is also true for the split second before
  // the fetch below resolves, and landing here wanting the "secondary" tab (e.g. from
  // SecondaryAssumptionResponseNew's "Prossima assunzione") must not get bounced back to
  // "primary" during that window, before it is actually known whether the tab even applies.
  const [secondaryTherapiesLoaded, setSecondaryTherapiesLoaded] = useState<boolean>(false)
  const [activeTab, setActiveTab] = useState<DiarySubTab>(location.state?.tab ?? (showPrimaryTab ? 'primary' : 'secondary'))

  // Also called back by the panel after a dose "al bisogno" (therapy-service
  // specs/2026-10-as-needed-assumptions.md), which may create the therapy it belongs to.
  const fetchSecondaryTherapies = useCallback(() => {
    if (!showSecondaryTab) {
      setSecondaryTherapiesLoaded(true)
      return
    }
    // No-op status setter: a failure just leaves the panel on its empty state.
    apiClient.getSecondaryTherapies(() => {})
      .then((data: Record<string, any>[]) => setSecondaryTherapies(Array.isArray(data) ? data : []))
      .catch(() => {})
      .finally(() => setSecondaryTherapiesLoaded(true))
  }, [showSecondaryTab])

  useEffect(() => { fetchSecondaryTherapies() }, [fetchSecondaryTherapies])

  // The tab is there even with no accessory therapy at all: its "Assunzione al bisogno" button
  // records a dose of any drug, creating the therapy on the way. Only once loaded, so a
  // still-PENDING fetch does not make it flash in.
  const hasSecondaryTherapies = showSecondaryTab && secondaryTherapiesLoaded

  useEffect(() => {
    if (activeTab === 'primary' && !showPrimaryTab) {
      setActiveTab('secondary')
    } else if (secondaryTherapiesLoaded && activeTab === 'secondary' && !hasSecondaryTherapies && showPrimaryTab) {
      setActiveTab('primary')
    }
  }, [activeTab, hasSecondaryTherapies, secondaryTherapiesLoaded, showPrimaryTab])

  if (!showPrimaryTab && secondaryTherapiesLoaded && !hasSecondaryTherapies) {
    return <EmptyState title="Nessun risultato" description="Nessun dato disponibile" />
  }

  return (
    <Box>
      <Tabs
        value={activeTab}
        onChange={(_e, val: DiarySubTab) => setActiveTab(val)}
        sx={{
          mb: 3,
          '& .MuiTab-root': {fontSize: 13, textTransform: 'none', minHeight: 40},
          '& .MuiTabs-indicator': {height: 2},
        }}
      >
        {showPrimaryTab && <Tab value="primary" label={sidebarItems?.therapyDiaryTherapies?.label ?? 'Terapie'} />}
        {hasSecondaryTherapies && (
          <Tab value="secondary" label={sidebarItems?.therapyDiarySecondaryTherapies?.label ?? 'Altre terapie'} />
        )}
      </Tabs>

      {activeTab === 'primary' && showPrimaryTab && <PrimaryAssumptionsPanel />}
      {activeTab === 'secondary' && hasSecondaryTherapies && (
        <SecondaryAssumptionsPanel
          secondaryTherapies={secondaryTherapies}
          initialTherapyId={location.state?.secondaryTherapyId}
          onSecondaryTherapiesChanged={fetchSecondaryTherapies}
        />
      )}
    </Box>
  )
}
